import { AGENT_URL } from '@/config/env'
// Value imports (a real class and a function), unlike the type-only imports below.
import {
  ASSIGNMENT_RUN_TRIGGERS,
  BOOKING_TOOLS,
  DutyToolError,
  WorkflowInvokeError,
} from '@/features/duties/types'
import { parseFactsSchema } from '@/features/workflows/facts'
import {
  isRecord,
  mutateJsonWithAuth as mutateWithStructuredErrors,
  fetchJsonWithAuth as fetchWithStructuredErrors,
} from '@/lib/api'
import { DutyBranch } from '@/features/duties/branches'
import type { AuthMethod } from '@/features/tools/types'
import { PHONE_VERIFY_FIELDS } from '@/features/tools/components/authenticate-caller/authConstants'
import type {
  BookingToolName,
  DutyAssignmentLogic,
  DutyAssignmentRun,
  DutyExclusionLogic,
  DutyCallVolumeMonitor,
  DutyCallVolumeMonitorConfig,
  DutyPatientMemoryDetail,
  DutyPatientMemoryInstruction,
  DutyPatientMemoryPatch,
  DutyPatientMemoryScope,
  DutyTestMemories,
  TestMemoryEntry,
  AppointmentType,
  DutyBookingProviders,
  DutyBranchPromotionPreview,
  DutyConnectionInfo,
  DutyConnectionWindow,
  DutyConversationMetrics,
  DutyFactExtractionMetrics,
  DutyFactsMap,
  DutyFactsTemplateInfo,
  DutyLabelMappingMetrics,
  DutyResequenceAnalysisPrompt,
  DutySmsConfig,
  DutyStatus,
  DutyTextingConfig,
  DutySystemScriptPatchArgs,
  DutySystemScriptPayload,
  DutySystemScriptPrompt,
  DutyOverridableFact,
  DutyTestPatientInfo,
  DutyToolKey,
  DutyToolsInfo,
  DutyWorkflow,
  DutyWorkflowsInfo,
  InvokeWorkflowResult,
  WorkflowFactError,
  DutyTransferCallSettings,
  TransferDestination,
  DutyVoicemailMetrics,
  DutyAssistantOverrides,
  DutyAssistantOverridesPatchArgs,
  OrgPhoneNumber,
} from '@/features/duties/types'

type BranchlessDutyFetcher<T> = (
  token: string,
  dutyId: string,
  signal?: AbortSignal
) => Promise<T>

type BranchAwareDutyFetcher<T> = (
  token: string,
  dutyId: string,
  branch: DutyBranch,
  signal?: AbortSignal
) => Promise<T>

type DutyQueryParams = Record<string, string | undefined>

function asString(value: unknown): string | null {
  return typeof value === 'string' ? value : null
}

function asNumber(value: unknown): number | null {
  return typeof value === 'number' ? value : null
}

function toFactsMap(value: unknown): DutyFactsMap {
  if (!isRecord(value)) return {}
  const out: DutyFactsMap = {}
  for (const [k, v] of Object.entries(value)) {
    if (typeof v === 'string') out[k] = v
    else if (v == null) out[k] = null
    else out[k] = JSON.stringify(v)
  }
  return out
}

function asStringArray(value: unknown): string[] {
  if (!Array.isArray(value)) return []
  return value.filter((item): item is string => typeof item === 'string')
}

function parseDutyTestPatientRow(value: unknown) {
  const obj = isRecord(value) ? value : {}
  const patientIds = isRecord(obj.patient_ids)
    ? (obj.patient_ids as Record<string, unknown>)
    : null
  return {
    id: asString(obj.id),
    first_name: asString(obj.first_name),
    last_name: asString(obj.last_name),
    full_name: asString(obj.full_name),
    date_of_birth: asString(obj.date_of_birth),
    address: asString(obj.address),
    language: asString(obj.language),
    source_id: asString(obj.source_id),
    patient_ids: patientIds,
  }
}

function parseDutyOverridableFacts(
  value: unknown
): DutyOverridableFact[] | null {
  if (!Array.isArray(value)) return null
  return value.flatMap((item) => {
    if (!isRecord(item) || typeof item.fact_name !== 'string') return []
    return [
      {
        fact_name: item.fact_name,
        value_type: asString(item.value_type),
        has_value: item.has_value === true,
        value: item.value ?? null,
        override: item.override ?? null,
      },
    ]
  })
}

function parseDutyTestCallValidation(value: unknown) {
  const obj = isRecord(value) ? value : {}
  return {
    can_launch: obj.can_launch !== false,
    conflicting_keys: asStringArray(obj.conflicting_keys),
    errors: asStringArray(obj.errors),
  }
}

async function fetchJsonWithAuth<T>({
  token,
  url,
  signal,
  errorMessage,
}: {
  token: string
  url: string
  signal?: AbortSignal
  errorMessage: string
}): Promise<T> {
  const response = await fetch(url, {
    headers: { Authorization: token },
    signal,
  })

  if (!response.ok) {
    const text = await response.text().catch(() => '')
    throw new Error(text || `${errorMessage}: ${response.status}`)
  }

  return (await response.json()) as T
}

async function mutateJsonWithAuth<T>({
  token,
  url,
  method,
  body,
  errorMessage,
}: {
  token: string
  url: string
  method: 'PATCH' | 'POST' | 'PUT' | 'DELETE'
  body?: unknown
  errorMessage: string
}): Promise<T> {
  const response = await fetch(url, {
    method,
    headers: {
      Authorization: token,
      'Content-Type': 'application/json',
    },
    body: typeof body === 'undefined' ? undefined : JSON.stringify(body),
  })

  if (!response.ok) {
    const text = await response.text().catch(() => '')
    throw new Error(text || `${errorMessage}: ${response.status}`)
  }

  // Guard against 204 No Content or empty-body responses where .json() would throw.
  const contentLength = response.headers.get('content-length')
  if (response.status === 204 || contentLength === '0') {
    return undefined as T
  }

  return (await response.json()) as T
}

export function dutyDetailUrl(
  dutyId: string,
  path: string,
  params?: DutyQueryParams
) {
  const normalized = path.replace(/^\//, '')
  const url = `${AGENT_URL}/data/duties/${encodeURIComponent(dutyId)}/${normalized}`
  const search = new URLSearchParams()
  for (const [key, value] of Object.entries(params ?? {})) {
    if (value !== undefined) search.set(key, value)
  }
  const query = search.toString()
  return query ? `${url}?${query}` : url
}

function makeDutyFetcher<T>(
  path: string,
  errorMessage: string,
  parse: (raw: unknown) => T,
  params?: DutyQueryParams
): BranchlessDutyFetcher<T> {
  return async (token, dutyId, signal) => {
    const raw = await fetchJsonWithAuth<unknown>({
      token,
      url: dutyDetailUrl(dutyId, path, params),
      signal,
      errorMessage,
    })
    return parse(raw)
  }
}

function makeDutyScriptFetcher<T>(
  path: string,
  errorMessage: string,
  parse: (raw: unknown) => T
): BranchAwareDutyFetcher<T> {
  return async (token, dutyId, branch, signal) => {
    const raw = await fetchJsonWithAuth<unknown>({
      token,
      url: dutyDetailUrl(dutyId, path, { branch }),
      signal,
      errorMessage,
    })
    return parse(raw)
  }
}

// ---- Low-level fetchers (one per card) ----

export const fetchDutyConnectionInfo = makeDutyFetcher<DutyConnectionInfo>(
  'connection',
  'Failed to fetch duty connection',
  (raw) => {
    const obj = isRecord(raw) ? raw : {}

    const available_phone_numbers: OrgPhoneNumber[] = []
    if (Array.isArray(obj.available_phone_numbers)) {
      for (const item of obj.available_phone_numbers) {
        if (
          isRecord(item) &&
          typeof item.id === 'string' &&
          typeof item.number === 'string'
        ) {
          available_phone_numbers.push({
            id: item.id,
            number: item.number,
            label: typeof item.label === 'string' ? item.label : undefined,
          })
        }
      }
    }

    return {
      phone_number: asString(obj.phone_number),
      phone_number_id: asString(obj.phone_number_id),
      phone_number_label: asString(obj.phone_number_label),
      timezone: asString(obj.timezone),
      windows: isRecord(obj.windows)
        ? (obj.windows as Record<string, DutyConnectionWindow>)
        : {},
      available_phone_numbers,
      is_operator: obj.is_operator === true,
      holidays: asStringArray(obj.holidays),
      org_holidays: asStringArray(obj.org_holidays),
      holidays_overridden: obj.holidays_overridden === true,
    }
  }
)

const DUTY_TOOL_KEYS: ReadonlySet<DutyToolKey> = new Set<DutyToolKey>([
  'mercury:booking',
  'complain',
  'voicemail',
  'transfer_call',
  'labels',
  'facts_extraction',
  'resequence',
  'handoff_to_duty',
  'authenticate_person',
  'fast_first_message',
  'patient_memory',
  'mercury:encounter',
  'mercury:patient',
])

function parsePhoneLookupPolicy(
  value: unknown
): false | { verify_with_any: string[] } | { unrecognized: unknown } {
  if (value === undefined || value === false) return false
  const fields = isRecord(value) ? value.verify_with_any : undefined
  if (
    !isRecord(value) ||
    Object.keys(value).length !== 1 ||
    !Array.isArray(fields) ||
    fields.length === 0 ||
    !fields.every(
      (field): field is string =>
        typeof field === 'string' &&
        PHONE_VERIFY_FIELDS.some((allowed) => allowed === field)
    )
  ) {
    return { unrecognized: value }
  }
  return { verify_with_any: fields }
}

export const fetchDutyToolsInfo = makeDutyFetcher<DutyToolsInfo>(
  'tools',
  'Failed to fetch duty tools',
  (raw) => {
    const obj = isRecord(raw) ? raw : {}

    const toolsRaw = obj.tools
    const tools: DutyToolKey[] = []
    if (Array.isArray(toolsRaw)) {
      const seen = new Set<DutyToolKey>()
      for (const item of toolsRaw) {
        if (typeof item !== 'string') continue
        const key = item.trim() as DutyToolKey
        if (!DUTY_TOOL_KEYS.has(key)) continue
        if (seen.has(key)) continue
        seen.add(key)
        tools.push(key)
      }
    }

    let transfer_call_settings: DutyTransferCallSettings | undefined
    if (isRecord(obj.transfer_call_settings)) {
      const tcs = obj.transfer_call_settings as Record<string, unknown>
      // Warm/cold is derived per-destination from each destination's own
      // transferPlan (see useTransferCallDrafts). No card-level scalar.
      const destinations = Array.isArray(tcs.destinations)
        ? tcs.destinations.filter((d): d is TransferDestination => isRecord(d))
        : []
      transfer_call_settings = { destinations }
    }

    const result: DutyToolsInfo = {
      tools,
      is_operator: obj.is_operator === true,
    }
    if (transfer_call_settings)
      result.transfer_call_settings = transfer_call_settings
    if (isRecord(obj.handoff_to_duty_settings)) {
      const h2d = obj.handoff_to_duty_settings as Record<string, unknown>
      result.handoff_to_duty_settings = {
        config: isRecord(h2d.config)
          ? (h2d.config as Record<string, unknown>)
          : null,
      }
    }
    if (isRecord(obj.authenticate_person_settings)) {
      const aps = obj.authenticate_person_settings as Record<string, unknown>
      result.authenticate_person_settings = {
        methods: Array.isArray(aps.methods)
          ? (aps.methods as AuthMethod[])
          : [],
        allowed_persons: Array.isArray(aps.allowed_persons)
          ? (aps.allowed_persons as string[])
          : ['self'],
        org_methods: Array.isArray(aps.org_methods)
          ? (aps.org_methods as AuthMethod[])
          : null,
        org_allowed_persons: Array.isArray(aps.org_allowed_persons)
          ? (aps.org_allowed_persons as string[])
          : null,
        methods_overridden: aps.methods_overridden === true,
        persons_overridden: aps.persons_overridden === true,
        phone_lookup: parsePhoneLookupPolicy(aps.phone_lookup),
        numeric_confirmation:
          aps.numeric_confirmation === undefined
            ? false
            : typeof aps.numeric_confirmation === 'boolean'
              ? aps.numeric_confirmation
              : { unrecognized: aps.numeric_confirmation },
      }
    }
    return result
  }
)

export const fetchDutyWorkflows = makeDutyFetcher<DutyWorkflowsInfo>(
  'workflows',
  'Failed to fetch duty workflows',
  (raw) => {
    const obj = isRecord(raw) ? raw : {}
    const workflows: DutyWorkflow[] = []
    if (Array.isArray(obj.workflows)) {
      for (const item of obj.workflows) {
        if (!isRecord(item) || typeof item.slug !== 'string') continue
        workflows.push({
          slug: item.slug,
          org_slug: typeof item.org_slug === 'string' ? item.org_slug : null,
          title: typeof item.title === 'string' ? item.title : null,
          trigger: typeof item.trigger === 'string' ? item.trigger : null,
          description:
            typeof item.description === 'string' ? item.description : null,
          // Only an explicit `true` retires a workflow, so a payload predating
          // the field reads as live rather than deprecated.
          deprecated: item.deprecated === true,
          facts_schema: parseFactsSchema(item.facts_schema),
        })
      }
    }
    return { workflows }
  }
)

/**
 * Pull fact errors out of an invoke failure body. Nebula keys them by fact name:
 * `{"patient_mrn": "Missing required fact."}`.
 */
function parseFactErrors(raw: unknown): WorkflowFactError[] {
  if (!isRecord(raw)) return []
  return Object.entries(raw).map(([field, message]) => ({
    field,
    message: typeof message === 'string' ? message : JSON.stringify(message),
  }))
}

/**
 * Start a Nova session for one of the duty's registered workflows.
 *
 * Throws `WorkflowInvokeError` carrying Nebula's per-fact validation errors, so
 * the form can mark the offending inputs instead of only showing a toast.
 */
export async function invokeDutyWorkflow({
  token,
  dutyId,
  slug,
  facts,
}: {
  token: string
  dutyId: string
  slug: string
  facts: Record<string, string>
}): Promise<InvokeWorkflowResult> {
  const response = await fetch(
    `${AGENT_URL}/data/duties/${dutyId}/workflows/${encodeURIComponent(slug)}/invoke`,
    {
      method: 'POST',
      headers: {
        Authorization: token,
        'Content-Type': 'application/json',
      },
      body: JSON.stringify({ facts }),
    }
  )

  const payload: unknown = await response.json().catch(() => null)

  if (!response.ok) {
    const obj = isRecord(payload) ? payload : {}
    const message =
      typeof obj.error === 'string'
        ? obj.error
        : `Failed to create session: ${response.status}`
    throw new WorkflowInvokeError(message, parseFactErrors(obj.errors))
  }

  const obj = isRecord(payload) ? payload : {}
  if (
    typeof obj.session_id !== 'string' ||
    obj.session_id.trim().length === 0
  ) {
    throw new WorkflowInvokeError('Nebula did not return a session id')
  }
  return {
    session_id: obj.session_id,
    status: typeof obj.status === 'string' ? obj.status : null,
    reused: obj.reused === true,
  }
}

/**
 * Replaces the list of people a duty is shared with. An empty array restores
 * org-wide visibility; a non-empty one narrows the duty to those people. The
 * server folds the caller into a non-empty list and lowercases the addresses,
 * so the response is the authority on what was stored — not what was sent.
 * `expected` is the list the caller started from; the server answers 409 if
 * sharing changed since.
 */
export async function patchDutyAccess({
  token,
  dutyId,
  access,
  expected,
}: {
  token: string
  dutyId: string
  access: string[]
  expected: string[]
}): Promise<{ access: string[] }> {
  return mutateJsonWithAuth({
    token,
    url: dutyDetailUrl(dutyId, 'access'),
    method: 'PATCH',
    body: { access, expected },
    errorMessage: 'Failed to update duty access',
  })
}

export async function patchDutyHandoffConfig({
  token,
  dutyId,
  destinations,
}: {
  token: string
  dutyId: string
  destinations: Record<string, unknown>[]
}): Promise<{ success?: boolean; handoff_config?: unknown }> {
  return mutateJsonWithAuth({
    token,
    url: dutyDetailUrl(dutyId, 'handoff-config'),
    method: 'PATCH',
    body: { destinations },
    errorMessage: 'Failed to update duty handoff config',
  })
}

export type PatchDutyCallVolumeMonitorConfigPayload = Partial<
  Omit<
    DutyCallVolumeMonitorConfig,
    'auto_resolve' | 'notifications' | 'warmup_grace_minutes'
  >
> & {
  notifications?: Partial<DutyCallVolumeMonitorConfig['notifications']>
}

export async function patchDutyCallVolumeMonitor(
  token: string,
  dutyId: string,
  config: PatchDutyCallVolumeMonitorConfigPayload
): Promise<DutyCallVolumeMonitor> {
  return await mutateJsonWithAuth<DutyCallVolumeMonitor>({
    token,
    url: dutyDetailUrl(dutyId, 'monitors/call-volume'),
    method: 'PATCH',
    body: { config },
    errorMessage: 'Failed to update call volume monitor',
  })
}

export async function testDutyCallVolumeMonitorNotification(
  token: string,
  dutyId: string
): Promise<{
  success: boolean
  provider_message_id?: string | null
  notification?: Record<string, unknown>
}> {
  return await mutateJsonWithAuth<{
    success: boolean
    provider_message_id?: string | null
    notification?: Record<string, unknown>
  }>({
    token,
    url: dutyDetailUrl(dutyId, 'monitors/call-volume/test-notification'),
    method: 'POST',
    errorMessage: 'Failed to send call volume monitor test notification',
  })
}

export async function resolveDutyCallVolumeMonitor(
  token: string,
  dutyId: string
): Promise<DutyCallVolumeMonitor> {
  return await mutateJsonWithAuth<DutyCallVolumeMonitor>({
    token,
    url: dutyDetailUrl(dutyId, 'monitors/call-volume/resolve'),
    method: 'POST',
    errorMessage: 'Failed to resolve call volume monitor',
  })
}

export const fetchDutyResequenceAnalysisPrompt =
  makeDutyScriptFetcher<DutyResequenceAnalysisPrompt>(
    'resequence-analysis-prompt',
    'Failed to fetch duty resequence prompt',
    (raw) => {
      const obj = isRecord(raw) ? raw : {}
      return { prompt: asString(obj.prompt) }
    }
  )

export const fetchDutyConversationMetrics =
  makeDutyScriptFetcher<DutyConversationMetrics>(
    'conversation',
    'Failed to fetch duty conversation',
    (raw) => {
      const obj = isRecord(raw) ? raw : {}
      return {
        characters: asNumber(obj.characters),
        complexity: asString(obj.complexity),
        script: asString(obj.script),
      }
    }
  )

export const fetchDutyVoicemailMetrics =
  makeDutyScriptFetcher<DutyVoicemailMetrics>(
    'voicemail',
    'Failed to fetch duty voicemail',
    (raw) => {
      const obj = isRecord(raw) ? raw : {}
      return {
        duration: asNumber(obj.duration),
        characters: asNumber(obj.characters),
        script: asString(obj.script),
      }
    }
  )

export const fetchDutyLabelMappingMetrics =
  makeDutyScriptFetcher<DutyLabelMappingMetrics>(
    'label-mapping',
    'Failed to fetch duty label mapping',
    (raw) => {
      const obj = isRecord(raw) ? raw : {}
      return {
        labels: asNumber(obj.labels),
        script: asString(obj.script),
      }
    }
  )

export const fetchDutyTestPatientInfo = makeDutyFetcher<DutyTestPatientInfo>(
  'test-patient',
  'Failed to fetch duty test client',
  (raw) => {
    const obj = isRecord(raw) ? raw : {}
    return {
      phone_number: asString(obj.phone_number),
      org_facts: toFactsMap(obj.org_facts),
      client_facts: toFactsMap(obj.client_facts),
      test_patient: isRecord(obj.test_patient)
        ? parseDutyTestPatientRow(obj.test_patient)
        : null,
      overridable_facts: parseDutyOverridableFacts(obj.overridable_facts),
      validation: parseDutyTestCallValidation(obj.validation),
      rendered_test_facts: asString(obj.rendered_test_facts),
      parsed_test_facts: toFactsMap(obj.parsed_test_facts),
      variable_values: isRecord(obj.variable_values)
        ? (obj.variable_values as Record<string, unknown>)
        : null,
    }
  }
)

export const fetchDutyFactExtractionMetrics =
  makeDutyScriptFetcher<DutyFactExtractionMetrics>(
    'fact-extraction',
    'Failed to fetch duty fact extraction',
    (raw) => {
      const obj = isRecord(raw) ? raw : {}
      return {
        facts_as_string: asString(obj.facts_as_string),
        facts_count: asNumber(obj.facts_count),
      }
    }
  )

export const fetchDutyFactsTemplate =
  makeDutyScriptFetcher<DutyFactsTemplateInfo>(
    'facts-template',
    'Failed to fetch duty facts template',
    (raw) => {
      const obj = isRecord(raw) ? raw : {}
      return { template: asString(obj.template) }
    }
  )

export const fetchDutySystemScript =
  makeDutyScriptFetcher<DutySystemScriptPayload>(
    'system-script',
    'Failed to fetch duty system script',
    (raw) => {
      if (!isRecord(raw)) {
        return { separated_scripts: false, outbound: null, inbound: null }
      }

      const obj = raw as Record<string, unknown>

      const normalizePrompt = (
        value: unknown
      ): DutySystemScriptPrompt | null => {
        if (!isRecord(value)) return null
        const v = value as Record<string, unknown>
        return {
          prompt: asString(v.prompt),
          prompt_name: asString(v.prompt_name),
        }
      }

      return {
        separated_scripts: Boolean(obj.separated_scripts),
        outbound: normalizePrompt(obj.outbound),
        inbound: normalizePrompt(obj.inbound),
      }
    }
  )

export async function patchDutyConversationScript(
  token: string,
  dutyId: string,
  script: string
) {
  return await mutateWithStructuredErrors<{ script: string }>({
    token,
    url: dutyDetailUrl(dutyId, 'conversation'),
    method: 'PATCH',
    body: { script },
    errorMessage: 'Failed to update duty conversation script',
  })
}

export async function patchDutyVoicemailScript(
  token: string,
  dutyId: string,
  script: string
) {
  return await mutateWithStructuredErrors<{ script: string }>({
    token,
    url: dutyDetailUrl(dutyId, 'voicemail'),
    method: 'PATCH',
    body: { script },
    errorMessage: 'Failed to update duty voicemail script',
  })
}

export async function patchDutyLabelMappingScript(
  token: string,
  dutyId: string,
  script: string
) {
  return await mutateWithStructuredErrors<{ script: string }>({
    token,
    url: dutyDetailUrl(dutyId, 'label-mapping'),
    method: 'PATCH',
    body: { script },
    errorMessage: 'Failed to update duty label mapping script',
  })
}

export async function patchDutyFactExtractionScript(
  token: string,
  dutyId: string,
  script: string
) {
  return await mutateWithStructuredErrors<{
    facts_as_string: string
    facts_count: number
  }>({
    token,
    url: dutyDetailUrl(dutyId, 'fact-extraction'),
    method: 'PATCH',
    body: { script },
    errorMessage: 'Failed to update duty fact extraction script',
  })
}

export async function patchDutyFactsTemplateScript(
  token: string,
  dutyId: string,
  script: string
) {
  return await mutateWithStructuredErrors<{ template: string }>({
    token,
    url: dutyDetailUrl(dutyId, 'facts-template'),
    method: 'PATCH',
    body: { script },
    errorMessage: 'Failed to update duty facts template',
  })
}

export async function patchDutyResequenceAnalysisPrompt(
  token: string,
  dutyId: string,
  prompt: string
) {
  return await mutateJsonWithAuth<{ prompt: string }>({
    token,
    url: dutyDetailUrl(dutyId, 'resequence-analysis-prompt'),
    method: 'PATCH',
    body: { prompt },
    errorMessage: 'Failed to update duty resequence analysis prompt',
  })
}

export const fetchDutySmsConfig = makeDutyFetcher<DutySmsConfig>(
  'messaging-prompts',
  'Failed to fetch duty messaging prompts',
  (raw) => {
    const obj = isRecord(raw) ? raw : {}
    return {
      inbound_message_prompt: asString(obj.inbound_message_prompt),
    }
  }
)

export async function patchDutySmsConfig(
  token: string,
  dutyId: string,
  data: Partial<DutySmsConfig>
) {
  return await mutateJsonWithAuth<DutySmsConfig>({
    token,
    url: dutyDetailUrl(dutyId, 'messaging-prompts'),
    method: 'PATCH',
    body: data,
    errorMessage: 'Failed to update duty messaging prompts',
  })
}

export async function patchDutySystemScript(
  token: string,
  dutyId: string,
  args: DutySystemScriptPatchArgs
) {
  return await mutateWithStructuredErrors<DutySystemScriptPayload>({
    token,
    url: dutyDetailUrl(dutyId, 'system-script'),
    method: 'PATCH',
    body: args,
    errorMessage: 'Failed to update duty system script',
  })
}

export const fetchDutyTextingConfig = makeDutyFetcher<DutyTextingConfig>(
  'texting-config',
  'Failed to fetch duty texting config',
  (raw) => {
    const obj = isRecord(raw) ? raw : {}
    return {
      is_texting_only: obj.is_texting_only === true,
      handled_by_angelica: obj.handled_by_angelica === true,
    }
  }
)

export async function patchDutyTextingConfig(
  token: string,
  dutyId: string,
  data: Partial<DutyTextingConfig>
) {
  return await mutateJsonWithAuth<DutyTextingConfig>({
    token,
    url: dutyDetailUrl(dutyId, 'texting-config'),
    method: 'PATCH',
    body: data,
    errorMessage: 'Failed to update duty texting config',
  })
}

// ---- New-duty helpers ----

export type NewDutyData = {
  phone_numbers: { id: string; number: string }[]
  test_patient:
    | { id: string; name: string; date_of_birth?: string | null }[]
    | null
  test_provider: { id: string; name: string }[] | null
  providers: { id: string; name: string }[]
}

export async function fetchNewDutyData(
  token: string,
  signal?: AbortSignal
): Promise<NewDutyData> {
  return fetchJsonWithAuth<NewDutyData>({
    token,
    url: `${AGENT_URL}/data/duties/new-duty`,
    signal,
    errorMessage: 'Failed to fetch new duty data',
  })
}

/** The scripts a new duty starts from, as last saved from the config page. */
export type DutyTemplates = Record<string, string>

export async function fetchDutyTemplates(
  token: string,
  signal?: AbortSignal
): Promise<DutyTemplates> {
  const body = await fetchJsonWithAuth<{ templates: DutyTemplates }>({
    token,
    url: `${AGENT_URL}/duty-templates`,
    signal,
    errorMessage: 'Failed to fetch the duty templates',
  })
  return body.templates
}

export async function createDuty(
  token: string,
  payload: Record<string, unknown>
): Promise<{ id: string; [key: string]: unknown }> {
  return mutateJsonWithAuth({
    token,
    url: `${AGENT_URL}/data/duties`,
    method: 'POST',
    body: payload,
    errorMessage: 'Failed to create duty',
  })
}

export async function addPatientsToDuty(
  token: string,
  dutyId: string,
  patientIds: string[]
): Promise<{ success: boolean; message: string }> {
  return mutateJsonWithAuth({
    token,
    url: `${AGENT_URL}/data/duties/${dutyId}/patients/add`,
    method: 'PUT',
    body: { patientIds },
    errorMessage: 'Failed to assign clients to duty',
  })
}

// ---- Branches ----

/**
 * The branches this duty has. Live always exists; Edit and Review exist only
 * once a promotion has created them. No `branch` parameter — the question is
 * about the duty, not about one copy of it.
 */
export async function fetchDutyBranches(
  token: string,
  dutyId: string,
  signal?: AbortSignal
): Promise<DutyBranch[]> {
  const raw = await fetchJsonWithAuth<unknown>({
    token,
    url: dutyDetailUrl(dutyId, 'branches'),
    signal,
    errorMessage: 'Failed to fetch duty branches',
  })
  return parseDutyBranches(raw)
}

export async function fetchDutyBranchPromotionPreview(
  token: string,
  dutyId: string,
  signal?: AbortSignal
): Promise<DutyBranchPromotionPreview> {
  return fetchWithStructuredErrors<DutyBranchPromotionPreview>({
    token,
    url: dutyDetailUrl(dutyId, 'branch-promotions/preview'),
    signal,
    errorMessage: 'Failed to fetch Review-to-Live preview',
  })
}

export async function promoteDutyBranch(
  token: string,
  dutyId: string,
  source: DutyBranch,
  target: DutyBranch
): Promise<{ branches: DutyBranch[] }> {
  const raw = await mutateWithStructuredErrors<unknown>({
    token,
    url: dutyDetailUrl(dutyId, 'branch-promotions'),
    method: 'POST',
    body: { source, target },
    errorMessage: 'Failed to promote duty branch',
  })
  return { branches: parseDutyBranches(raw) }
}

function parseDutyBranches(raw: unknown): DutyBranch[] {
  const listed = isRecord(raw) ? asStringArray(raw.branches) : []
  const branches = Object.values(DutyBranch).filter((branch) =>
    listed.includes(branch)
  )
  return branches.includes(DutyBranch.Live)
    ? branches
    : [DutyBranch.Live, ...branches]
}

// ---- Duty version history ----

export async function fetchDutyVersions(
  token: string,
  dutyId: string,
  signal?: AbortSignal
): Promise<import('@/features/duties/types').DutyVersion[]> {
  return fetchJsonWithAuth({
    token,
    url: dutyDetailUrl(dutyId, 'versions'),
    signal,
    errorMessage: 'Failed to fetch duty versions',
  })
}

// ---- Summary fetcher (lightweight canvas data) ----

export interface DutySummary {
  id: string
  name: string | null
  computer_use: boolean
  status: DutyStatus
  patient_count: number
  // Current assignment population excludes closed, cancelled, null, and
  // unrecognized patient-duty statuses. Null is used only by placeholder data.
  assignment_population_count: number | null
  // Successful assignments include canonical `completed` and legacy `complete`.
  assignment_completed_count: number | null
  call_count: number
  // Number of Nova computer-use workflows assigned to this duty (via the
  // app_nova_workflow_duties junction). Drives whether the "Computer use" block
  // is shown.
  computer_use_workflow_count: number
  // Count of non-internal computer-use sessions run for this duty. Shown as the
  // value in the "Computer use" block.
  computer_use_session_count: number
  // Distinct slugs of the assigned Nova workflows, used to deep-link the block
  // to the sessions page filtered by those workflow types.
  computer_use_workflow_slugs: string[]
  // ISO timestamp of the last config edit; powers the "Updated X ago" label.
  config_updated_at: string | null
  // Ordered canvas blocks after "Client assignment", straight from
  // `app_duties.config.workflow_graph`. `unknown` rather than `string[]`
  // because that is the truth: the server passes this hand-edited JSON value
  // through untouched, so the only guarantee is that it parsed. Absent (the
  // common case) means the duty never overrode the canvas. resolveWorkflowBlocks
  // in @/features/duties/workflowGraph is the one thing that interprets it.
  workflow_graph: unknown
  // Mirrors app_duties.config.texting.is_texting_only. Config-only (set
  // directly in `config`, never written by the UI); drives whether the
  // canvas shows a "Texting" block instead of "Calling".
  is_texting_only: boolean
  // Mirrors app_duties.config.health_check. The canvas uses this to avoid
  // requesting or displaying calling health for duties that have not opted in.
  health_check_enabled: boolean
  // Emails the duty is shared with; empty means everyone in the org.
  access: string[]
}

export async function fetchDutySummary(
  token: string,
  dutyId: string,
  signal?: AbortSignal
): Promise<DutySummary> {
  return fetchJsonWithAuth<DutySummary>({
    token,
    url: `${AGENT_URL}/data/duties/${dutyId}`,
    signal,
    errorMessage: 'Failed to fetch duty summary',
  })
}

export interface DutyHealthCheck {
  health: boolean
  checked_at: string
}

export async function fetchDutyHealthCheck(
  token: string,
  dutyId: string,
  signal?: AbortSignal
): Promise<DutyHealthCheck | null> {
  return fetchJsonWithAuth<DutyHealthCheck | null>({
    token,
    url: `${AGENT_URL}/data/duties/${dutyId}/health-check`,
    signal,
    errorMessage: 'Failed to fetch duty health check',
  })
}

// ---- Compiled templates ----

export interface CompiledTemplatesResponse {
  call_variables: Record<string, unknown>
  voicemail_prompt: string
  assistant_prompt: string
}

export const fetchCompiledTemplates =
  makeDutyFetcher<CompiledTemplatesResponse>(
    'render-test-view',
    'Failed to fetch rendered test view',
    (raw) => {
      const obj = isRecord(raw) ? raw : {}
      return {
        call_variables: isRecord(obj.call_variables)
          ? (obj.call_variables as Record<string, unknown>)
          : {},
        voicemail_prompt:
          typeof obj.voicemail_prompt === 'string' ? obj.voicemail_prompt : '',
        assistant_prompt:
          typeof obj.assistant_prompt === 'string' ? obj.assistant_prompt : '',
      }
    }
  )

// ---- Mutation functions ----

export async function patchDutyStatus(
  token: string,
  dutyId: string,
  status: DutyStatus
): Promise<{ status: DutyStatus }> {
  return mutateJsonWithAuth<{ status: DutyStatus }>({
    token,
    url: dutyDetailUrl(dutyId, 'status'),
    method: 'PATCH',
    body: { status },
    errorMessage: 'Failed to update duty status',
  })
}

export async function patchDutyName(
  token: string,
  dutyId: string,
  name: string
) {
  return mutateJsonWithAuth({
    token,
    url: dutyDetailUrl(dutyId, 'name'),
    method: 'PATCH',
    body: { name },
    errorMessage: 'Failed to update duty name',
  })
}

export type PatchDutyConnectionPayload = {
  phone_number_id?: string | null
  phone_number?: string | null
  timezone?: string | null
  windows?: Record<string, DutyConnectionWindow | null>
  is_operator?: boolean
  /** A list sets the duty override; null clears it to inherit the org default. */
  holidays?: string[] | null
}

const DEFAULT_CALL_VOLUME_MONITOR_CONFIG: DutyCallVolumeMonitorConfig = {
  enabled: false,
  warning_threshold_pct: 70,
  critical_threshold_pct: 30,
  evaluation_window_minutes: 15,
  warmup_grace_minutes: 10,
  expected_calls: 100,
  notifications: {
    slack_channel_id: null,
    fallback_emails: [],
  },
  auto_resolve: true,
  auto_resolve_consecutive_ok: 2,
  critical_reminder_minutes: 60,
}
function parseCallVolumeMonitorConfig(
  raw: unknown
): DutyCallVolumeMonitorConfig {
  const obj = isRecord(raw) ? raw : {}
  const warning = asNumber(obj.warning_threshold_pct)
  const critical = asNumber(obj.critical_threshold_pct)
  const evalWindow = asNumber(obj.evaluation_window_minutes)
  const warmup = asNumber(obj.warmup_grace_minutes)
  const expected = asNumber(obj.expected_calls)
  const consecutiveOk = asNumber(obj.auto_resolve_consecutive_ok)
  const reminder = asNumber(obj.critical_reminder_minutes)
  const notifications = isRecord(obj.notifications) ? obj.notifications : {}

  return {
    mode: obj.mode === 'scheduled' ? 'scheduled' : 'rolling',
    scheduled_windows: Array.isArray(obj.scheduled_windows)
      ? obj.scheduled_windows.filter(isRecord).map((hours) => ({
          start_time: asString(hours.start_time) ?? '',
          end_time: asString(hours.end_time) ?? '',
          expected_calls: asNumber(hours.expected_calls) ?? 1,
        }))
      : [],
    enabled: obj.enabled === true,
    warning_threshold_pct:
      warning ?? DEFAULT_CALL_VOLUME_MONITOR_CONFIG.warning_threshold_pct,
    critical_threshold_pct:
      critical ?? DEFAULT_CALL_VOLUME_MONITOR_CONFIG.critical_threshold_pct,
    evaluation_window_minutes:
      evalWindow === 15 ||
      evalWindow === 30 ||
      evalWindow === 45 ||
      evalWindow === 60 ||
      evalWindow === 180 ||
      evalWindow === 1440
        ? evalWindow
        : DEFAULT_CALL_VOLUME_MONITOR_CONFIG.evaluation_window_minutes,
    warmup_grace_minutes:
      warmup ?? DEFAULT_CALL_VOLUME_MONITOR_CONFIG.warmup_grace_minutes,
    expected_calls:
      expected ?? DEFAULT_CALL_VOLUME_MONITOR_CONFIG.expected_calls,
    notifications: {
      slack_channel_id: asString(notifications.slack_channel_id),
      fallback_emails: asStringArray(notifications.fallback_emails),
    },
    auto_resolve:
      typeof obj.auto_resolve === 'boolean'
        ? obj.auto_resolve
        : DEFAULT_CALL_VOLUME_MONITOR_CONFIG.auto_resolve,
    auto_resolve_consecutive_ok:
      consecutiveOk ??
      DEFAULT_CALL_VOLUME_MONITOR_CONFIG.auto_resolve_consecutive_ok,
    critical_reminder_minutes: reminder,
  }
}

export const fetchDutyCallVolumeMonitor =
  makeDutyFetcher<DutyCallVolumeMonitor>(
    'monitors/call-volume',
    'Failed to fetch duty call volume monitor',
    (raw) => {
      const obj = isRecord(raw) ? raw : {}
      const state = isRecord(obj.state) ? obj.state : {}
      const metrics = isRecord(state.metrics) ? state.metrics : {}
      return {
        duty_id: typeof obj.duty_id === 'string' ? obj.duty_id : '',
        monitor_type: 'call_volume',
        config: parseCallVolumeMonitorConfig(obj.config),
        state: {
          state:
            state.state === 'warning' || state.state === 'critical'
              ? state.state
              : 'healthy',
          metrics: {
            sent_calls: asNumber(metrics.sent_calls) ?? undefined,
            expected_calls: asNumber(metrics.expected_calls) ?? undefined,
            ratio_pct: asNumber(metrics.ratio_pct) ?? undefined,
            evaluation_window_minutes:
              asNumber(metrics.evaluation_window_minutes) ?? undefined,
          },
          open_alert_started_at: asString(state.open_alert_started_at),
          last_transition_at: asString(state.last_transition_at),
          last_notified_at: asString(state.last_notified_at),
          consecutive_healthy_windows:
            asNumber(state.consecutive_healthy_windows) ?? undefined,
        },
      }
    }
  )
export async function patchDutyConnection(
  token: string,
  dutyId: string,
  data: PatchDutyConnectionPayload
): Promise<DutyConnectionInfo> {
  return await mutateJsonWithAuth<DutyConnectionInfo>({
    token,
    url: dutyDetailUrl(dutyId, 'connection'),
    method: 'PATCH',
    body: data,
    errorMessage: 'Failed to update connection',
  })
}

/**
 * Toggle one tool on a duty.
 *
 * A refusal the server explained is re-thrown as `DutyToolError`, because this
 * route can reject a toggle for a reason only it knows: Nebula registers a
 * workflow per (org, workflow), so enabling a workflow-backed tool a *different*
 * duty already holds answers 409 naming that duty. Flattening that into a
 * generic message would leave the switch snapping back with nothing to act on.
 * `mutateJsonWithAuth` throws the raw response body, which is JSON.
 */
export async function patchDutyTool(
  token: string,
  dutyId: string,
  tool: DutyToolKey,
  enabled: boolean
): Promise<{ success: boolean; tools: string[] }> {
  try {
    return await mutateJsonWithAuth({
      token,
      url: dutyDetailUrl(dutyId, 'tools'),
      method: 'PATCH',
      body: { tool, enabled },
      errorMessage: 'Failed to update tools',
    })
  } catch (error) {
    const message = serverErrorMessage(error)
    throw message === null ? error : new DutyToolError(message)
  }
}

/** The server's `error` field out of a thrown response body, or null if absent. */
function serverErrorMessage(error: unknown): string | null {
  if (!(error instanceof Error)) return null
  let parsed: unknown
  try {
    parsed = JSON.parse(error.message)
  } catch {
    // Not JSON — a transport failure, or a proxy answering with plain text.
    return null
  }
  if (typeof parsed !== 'object' || parsed === null) return null
  const message = (parsed as { error?: unknown }).error
  return typeof message === 'string' && message.trim() ? message : null
}

function parseAppointmentTypes(raw: unknown): AppointmentType[] {
  if (!Array.isArray(raw)) return []
  return raw.flatMap((value) => {
    if (!isRecord(value)) return []
    const name = asString(value.name)
    if (!name) return []
    const providers = Array.isArray(value.providers)
      ? value.providers.flatMap((provider) => {
          if (!isRecord(provider)) return []
          const id = asString(provider.scheduling_provider_id)
          if (!id) return []
          return [
            {
              scheduling_provider_id: id,
              provider: asString(provider.provider) ?? null,
              resource: asString(provider.resource) ?? null,
            },
          ]
        })
      : []
    const range = isRecord(value.booking_window) ? value.booking_window : null
    if (range && range.mode !== 'fixed' && range.mode !== 'relative')
      throw new Error('Invalid booking window mode')
    return [
      {
        name,
        visit_types: asStringArray(value.visit_types),
        duration_options_minutes: Array.isArray(value.duration_options_minutes)
          ? value.duration_options_minutes.filter(
              (minutes): minutes is number => typeof minutes === 'number'
            )
          : [],
        providers,
        booking_window: range
          ? range.mode === 'relative'
            ? {
                mode: 'relative',
                earliest_days:
                  typeof range.earliest_days === 'number'
                    ? range.earliest_days
                    : null,
                latest_days:
                  typeof range.latest_days === 'number'
                    ? range.latest_days
                    : null,
              }
            : {
                mode: 'fixed',
                start_date: asString(range.start_date) ?? null,
                end_date: asString(range.end_date) ?? null,
              }
          : null,
      },
    ]
  })
}

function parseDutyBookingProviders(raw: unknown): DutyBookingProviders {
  const obj = isRecord(raw) ? raw : {}
  const available_providers = Array.isArray(obj.available_providers)
    ? obj.available_providers.flatMap((value) => {
        if (!isRecord(value)) return []
        const id = asString(value.id)
        const name = asString(value.name)
        if (!id || !name) return []
        return [
          {
            id,
            name,
            specialty: asString(value.specialty),
            credentials: asString(value.credentials),
          },
        ]
      })
    : []
  if (
    !Array.isArray(obj.enabled_tools) ||
    !obj.enabled_tools.every((tool): tool is BookingToolName =>
      BOOKING_TOOLS.includes(tool as BookingToolName)
    )
  ) {
    throw new Error('Invalid booking tool selection')
  }
  return {
    enabled_tools: obj.enabled_tools,
    facility_ids: asStringArray(obj.facility_ids),
    scheduling_instructions: asString(obj.scheduling_instructions) ?? '',
    require_aligned_start: obj.require_aligned_start === true,
    write_to_ehr: obj.write_to_ehr === true,
    appointment_types: parseAppointmentTypes(obj.appointment_types),
    available_providers,
    has_more: obj.has_more === true,
  }
}

export async function fetchDutyBookingProviders(
  token: string,
  dutyId: string,
  search: string = '',
  signal?: AbortSignal
): Promise<DutyBookingProviders> {
  const normalizedSearch = search.trim()
  const raw = await fetchJsonWithAuth<unknown>({
    token,
    url: dutyDetailUrl(dutyId, 'tools-config/booking/providers', {
      search: normalizedSearch || undefined,
    }),
    signal,
    errorMessage: 'Failed to fetch booking providers',
  })
  return parseDutyBookingProviders(raw)
}

export async function patchDutyBookingProviders(
  token: string,
  dutyId: string,
  config: {
    enabled_tools: BookingToolName[]
    facility_ids: string[]
    scheduling_instructions: string
    require_aligned_start?: boolean
    write_to_ehr?: boolean
    appointment_types?: AppointmentType[]
  }
): Promise<{
  success: boolean
  enabled_tools: BookingToolName[]
  facility_ids: string[]
  scheduling_instructions: string
  require_aligned_start: boolean
  write_to_ehr: boolean
}> {
  return await mutateJsonWithAuth({
    token,
    url: dutyDetailUrl(dutyId, 'tools-config/booking/providers'),
    method: 'PATCH',
    body: config,
    errorMessage: 'Failed to update booking configuration',
  })
}

export async function patchDutyHandoffToDutyConfig(
  token: string,
  dutyId: string,
  config: Record<string, unknown> | null
): Promise<{ success: boolean; config: Record<string, unknown> | null }> {
  return await mutateJsonWithAuth({
    token,
    url: dutyDetailUrl(dutyId, 'tools-config/handoff-to-duty'),
    method: 'PATCH',
    body: { config },
    errorMessage: 'Failed to update handoff-to-duty config',
  })
}

export async function patchDutyAuthenticatePersonConfig(
  token: string,
  dutyId: string,
  config: Record<string, unknown> | null
): Promise<{ success: boolean; config: Record<string, unknown> | null }> {
  return await mutateJsonWithAuth({
    token,
    url: dutyDetailUrl(dutyId, 'tools-config/authenticate-person'),
    method: 'PATCH',
    body: { config },
    errorMessage: 'Failed to update authentication config',
  })
}

export async function fetchDutyWaypoints(
  token: string,
  dutyId: string,
  signal?: AbortSignal
): Promise<{ waypoints: import('../types').DutyWaypoints | null }> {
  return await fetchJsonWithAuth<{
    waypoints: import('../types').DutyWaypoints | null
  }>({
    token,
    url: dutyDetailUrl(dutyId, 'waypoints'),
    signal,
    errorMessage: 'Failed to fetch duty waypoints',
  })
}

export async function generateDutyWaypoints(
  token: string,
  dutyId: string
): Promise<{ waypoints: import('../types').DutyWaypoints }> {
  return await mutateJsonWithAuth<{
    waypoints: import('../types').DutyWaypoints
  }>({
    token,
    url: dutyDetailUrl(dutyId, 'waypoints/generate'),
    method: 'POST',
    errorMessage: 'Failed to generate waypoints',
  })
}

export async function suggestWaypointWhen(
  token: string,
  dutyId: string,
  name: string
): Promise<{ when: string }> {
  return await mutateJsonWithAuth<{ when: string }>({
    token,
    url: dutyDetailUrl(dutyId, 'waypoints/suggest-when'),
    method: 'POST',
    body: { name },
    errorMessage: 'Failed to suggest waypoint description',
  })
}

export async function patchDutyWaypoints(
  token: string,
  dutyId: string,
  waypoints: import('../types').Waypoint[]
): Promise<{ waypoints: import('../types').DutyWaypoints }> {
  return await mutateJsonWithAuth<{
    waypoints: import('../types').DutyWaypoints
  }>({
    token,
    url: dutyDetailUrl(dutyId, 'waypoints'),
    method: 'PATCH',
    body: { waypoints: { waypoints } },
    errorMessage: 'Failed to update waypoints',
  })
}

function parseAssistantOverridesResponse(raw: unknown): DutyAssistantOverrides {
  const envelope = isRecord(raw) ? raw : {}
  const cfg = isRecord(envelope.voice) ? envelope.voice : null
  const provider = cfg ? asString(cfg.provider) : null
  const voiceId = cfg ? asString(cfg.voiceId) : null
  const voice =
    provider && voiceId
      ? {
          provider,
          voiceId,
          model: asString(cfg?.model),
          experimentalControls: isRecord(cfg?.experimentalControls)
            ? (cfg.experimentalControls as Record<string, unknown>)
            : null,
        }
      : null
  const modelCfg = isRecord(envelope.model) ? envelope.model : null
  const modelProvider = modelCfg ? asString(modelCfg.provider) : null
  const modelId = modelCfg ? asString(modelCfg.model) : null
  const model =
    modelProvider && modelId
      ? {
          provider: modelProvider,
          model: modelId,
          max_tokens:
            typeof modelCfg?.max_tokens === 'number'
              ? modelCfg.max_tokens
              : null,
        }
      : null
  const modelCatalog = Array.isArray(envelope.model_catalog)
    ? envelope.model_catalog.flatMap((entry) => {
        if (!isRecord(entry)) return []
        const title = asString(entry.title)
        const providers = Array.isArray(entry.providers)
          ? entry.providers.filter(
              (provider): provider is string => typeof provider === 'string'
            )
          : []
        const models = Array.isArray(entry.models)
          ? entry.models.filter(
              (model): model is string => typeof model === 'string'
            )
          : []
        return title && providers.length > 0
          ? [{ title, providers, models }]
          : []
      })
    : []
  return {
    voice,
    model,
    modelCatalog,
    voiceCatalog: Array.isArray(envelope.voice_catalog)
      ? (envelope.voice_catalog.filter(
          (entry) =>
            isRecord(entry) &&
            Array.isArray(entry.providers) &&
            isRecord(entry.voiceId) &&
            Array.isArray(entry.voiceId.values) &&
            isRecord(entry.model) &&
            Array.isArray(entry.model.values) &&
            Array.isArray(entry.required)
        ) as import('../types').DutyVoiceCatalogEntry[])
      : [],
  }
}

export const fetchDutyAssistantOverrides =
  makeDutyFetcher<DutyAssistantOverrides>(
    'assistant-overrides',
    'Failed to fetch assistant overrides',
    parseAssistantOverridesResponse
  )

export const fetchDutyTestAssistantOverrides =
  makeDutyFetcher<DutyAssistantOverrides>(
    'assistant-overrides',
    'Failed to fetch assistant overrides',
    parseAssistantOverridesResponse,
    { is_test_override: 'true' }
  )

export async function patchDutyAssistantOverrides(
  token: string,
  dutyId: string,
  args: DutyAssistantOverridesPatchArgs,
  isTest: boolean = false
): Promise<void> {
  await mutateWithStructuredErrors<Record<string, unknown>>({
    token,
    url: dutyDetailUrl(dutyId, 'assistant-overrides'),
    method: 'PATCH',
    body: { ...args, is_test_override: isTest },
    errorMessage: 'Failed to update assistant overrides',
  })
}

export const fetchDutyFirstMessage = makeDutyFetcher<string | null>(
  'first-message',
  'Failed to fetch first message',
  (raw) => (isRecord(raw) ? asString(raw.first_message) : null)
)

function parsePatientMemoryInstruction(
  raw: unknown
): DutyPatientMemoryInstruction | null {
  if (!isRecord(raw)) return null
  if (raw.kind !== 'context' && raw.kind !== 'handoff_routing') return null
  if (typeof raw.text !== 'string') return null
  return { kind: raw.kind, text: raw.text }
}

function parsePatientMemorySettings(raw: unknown) {
  if (!isRecord(raw)) throw new Error('Invalid memory settings')
  const scope = raw.scope as DutyPatientMemoryScope
  if (scope !== 'current_duty' && scope !== 'organization') {
    throw new Error('Invalid memory scope')
  }
  if (
    typeof raw.include_past_duties !== 'boolean' ||
    typeof raw.lookback_days !== 'number' ||
    !Number.isInteger(raw.lookback_days) ||
    typeof raw.max_memories_per_duty !== 'number' ||
    !Number.isInteger(raw.max_memories_per_duty)
  ) {
    throw new Error('Invalid memory selection settings')
  }
  return {
    scope,
    include_past_duties: raw.include_past_duties,
    lookback_days: raw.lookback_days,
    max_memories_per_duty: raw.max_memories_per_duty,
    custom_instructions: asString(raw.custom_instructions),
  }
}

function parsePatientMemoryDetail(raw: unknown): DutyPatientMemoryDetail {
  const obj = isRecord(raw) ? raw : {}
  if (
    typeof obj.enabled !== 'boolean' ||
    !isRecord(obj.config) ||
    typeof obj.creation_model !== 'string' ||
    typeof obj.creation_prompt !== 'string'
  ) {
    throw new Error('Invalid memory configuration')
  }
  const base = parsePatientMemorySettings(obj.config)
  const testConfig =
    obj.config.test_config == null
      ? null
      : parsePatientMemorySettings(obj.config.test_config)
  const instructions = Array.isArray(obj.effective_instructions)
    ? obj.effective_instructions
        .map(parsePatientMemoryInstruction)
        .filter((v): v is DutyPatientMemoryInstruction => v !== null)
    : []
  const testInstructions = Array.isArray(obj.test_effective_instructions)
    ? obj.test_effective_instructions
        .map(parsePatientMemoryInstruction)
        .filter((v): v is DutyPatientMemoryInstruction => v !== null)
    : []
  const eligibleDuties = Array.isArray(obj.eligible_duties)
    ? obj.eligible_duties.flatMap((item) =>
        isRecord(item) &&
        typeof item.id === 'string' &&
        typeof item.name === 'string' &&
        (item.status === 'active' ||
          item.status === 'inactive' ||
          item.status === 'archived')
          ? [
              {
                id: item.id,
                name: item.name,
                status: item.status as 'active' | 'inactive' | 'archived',
              },
            ]
          : []
      )
    : []
  return {
    enabled: obj.enabled,
    config: { ...base, test_config: testConfig },
    creation_model: obj.creation_model,
    creation_prompt: obj.creation_prompt,
    effective_instructions: instructions,
    test_effective_instructions: testInstructions,
    eligible_duties: eligibleDuties,
  }
}

export const fetchDutyPatientMemory = makeDutyFetcher<DutyPatientMemoryDetail>(
  'patient-memory',
  'Failed to fetch memory configuration',
  parsePatientMemoryDetail
)

function parseDutyTestMemories(raw: unknown): DutyTestMemories {
  const obj = isRecord(raw) ? raw : {}
  if (typeof obj.test_patient_id !== 'string' || !Array.isArray(obj.entries)) {
    throw new Error('Invalid test memories')
  }
  const entries: TestMemoryEntry[] = obj.entries.flatMap((entry) =>
    isRecord(entry) &&
    typeof entry.duty_id === 'string' &&
    typeof entry.content === 'string'
      ? [{ duty_id: entry.duty_id, content: entry.content }]
      : []
  )
  return { test_patient_id: obj.test_patient_id, entries }
}

export const fetchDutyTestMemories = makeDutyFetcher<DutyTestMemories>(
  'test-memories',
  'Failed to fetch test memories',
  parseDutyTestMemories
)

/**
 * The duty's client-selection criteria. Read-only in the UI: writes go through
 * relay (which validates the SQL), so there is no matching patch helper here.
 */
export const fetchDutyAssignmentLogic = makeDutyFetcher<DutyAssignmentLogic>(
  'assignment-logic',
  'Failed to fetch assignment logic',
  (raw) => {
    const obj = isRecord(raw) ? raw : {}
    return {
      sql: asString(obj.client_assignment_sql),
      readable: asString(obj.client_assignment_sql_readable),
    }
  }
)

function parseAssignmentRun(raw: unknown): DutyAssignmentRun {
  const run = isRecord(raw) ? raw : {}
  const trigger = ASSIGNMENT_RUN_TRIGGERS.find((value) => value === run.trigger)
  const createdAt = asString(run.created_at)
  const assigned = asNumber(run.assigned_count)
  const reopened = asNumber(run.reopened_count)
  const closed = asNumber(run.closed_count)
  const excluded = asNumber(run.excluded_count)
  if (
    !trigger ||
    !createdAt ||
    assigned === null ||
    reopened === null ||
    closed === null ||
    excluded === null
  ) {
    throw new Error('Invalid assignment run')
  }
  return {
    trigger,
    created_at: createdAt,
    assigned_count: assigned,
    reopened_count: reopened,
    closed_count: closed,
    excluded_count: excluded,
  }
}

/** The newest run in a runs response, or null when it lists none. */
function parseNewestAssignmentRun(raw: unknown): DutyAssignmentRun | null {
  if (!isRecord(raw) || !Array.isArray(raw.data)) {
    throw new Error('Invalid assignment runs')
  }
  const [newest] = raw.data
  return newest === undefined ? null : parseAssignmentRun(newest)
}

/**
 * The newest run of the duty's assignment rules, or null when no run is
 * recorded (the log has no history from before it existed). Every committed
 * run is listed, including ones that moved nobody, so this is the last time the
 * rules ran, not the last change.
 */
export const fetchDutyLastAssignmentRun = makeDutyFetcher(
  'assignment-runs',
  'Failed to fetch the last assignment run',
  parseNewestAssignmentRun,
  { limit: '1' }
)

/** The newest run where the assignment rule moved someone, or null if none is recorded. */
export const fetchDutyLastAssignmentChange = makeDutyFetcher(
  'assignment-runs',
  'Failed to fetch the last assignment change',
  parseNewestAssignmentRun,
  { limit: '1', changed: 'assignment' }
)

/** The newest run where the exclusion rule moved someone, or null if none is recorded. */
export const fetchDutyLastExclusionChange = makeDutyFetcher(
  'assignment-runs',
  'Failed to fetch the last exclusion change',
  parseNewestAssignmentRun,
  { limit: '1', changed: 'exclusion' }
)

/**
 * The duty's exclusion criteria. Read-only in the UI: writes go through
 * David, which saves through relay's validated PATCH — there is no matching
 * patch helper here.
 */
export const fetchDutyExclusionLogic = makeDutyFetcher<DutyExclusionLogic>(
  'exclusion-logic',
  'Failed to fetch exclusion logic',
  (raw) => {
    const obj = isRecord(raw) ? raw : {}
    return {
      sql: asString(obj.client_exclusion_sql),
      readable: asString(obj.client_exclusion_sql_readable),
    }
  }
)

export async function patchDutyFirstMessage(
  token: string,
  dutyId: string,
  firstMessage: string | null
): Promise<string | null> {
  // Writes ride the generic duty PATCH proxy (hoku handlers/duties.py), which
  // merges `config` and busts the detail cache — no dedicated write endpoint.
  await mutateJsonWithAuth<unknown>({
    token,
    url: `${AGENT_URL}/duties/${dutyId}`,
    method: 'PATCH',
    body: { config: { firstMessage } },
    errorMessage: 'Failed to update first message',
  })
  return firstMessage
}

export async function patchDutyPatientMemory(
  token: string,
  dutyId: string,
  config: DutyPatientMemoryPatch
): Promise<DutyPatientMemoryDetail> {
  const raw = await mutateJsonWithAuth<unknown>({
    token,
    url: dutyDetailUrl(dutyId, 'patient-memory'),
    method: 'PATCH',
    body: config,
    errorMessage: 'Failed to update memory configuration',
  })
  return parsePatientMemoryDetail(raw)
}

export async function patchDutyTestMemories(
  token: string,
  dutyId: string,
  entries: TestMemoryEntry[]
): Promise<DutyTestMemories> {
  const raw = await mutateJsonWithAuth<unknown>({
    token,
    url: dutyDetailUrl(dutyId, 'test-memories'),
    method: 'PATCH',
    body: { entries },
    errorMessage: 'Failed to update test memories',
  })
  return parseDutyTestMemories(raw)
}

export type PatchDutyTestPatientPayload = {
  facts: Record<string, string>
  new_facts?: { section: 'org' | 'client'; key: string; value: string }[]
}

/**
 * Relay's check of the duty as saved: test-call problems it doesn't block
 * saves on, and a problem it would refuse. Both empty when the check passed
 * or couldn't run.
 */
export type DutyTestFactsSaveCheck = {
  warnings: string[]
  validation_error: string | null
}

/**
 * The test-call problems Relay reported on a save it accepted. Script card
 * saves and the test fact check both carry them as `warnings`.
 */
export function parseSaveWarnings(raw: unknown): string[] {
  return isRecord(raw) ? asStringArray(raw.warnings) : []
}

export function parseDutyTestFactsSaveCheck(
  raw: unknown
): DutyTestFactsSaveCheck {
  const obj = isRecord(raw) ? raw : {}
  return {
    warnings: parseSaveWarnings(raw),
    validation_error: asString(obj.validation_error),
  }
}

export async function patchDutyTestPatient(
  token: string,
  dutyId: string,
  data: PatchDutyTestPatientPayload
): Promise<DutyTestFactsSaveCheck> {
  const raw = await mutateJsonWithAuth<unknown>({
    token,
    url: dutyDetailUrl(dutyId, 'test-patient'),
    method: 'PATCH',
    body: data,
    errorMessage: 'Failed to update test client',
  })
  return parseDutyTestFactsSaveCheck(raw)
}
